// Chat attachments on screen: a message's photo/video grid (photos open in
// a lightbox, video plays inline) and file chips, and the composer's tray of
// uploads with progress, cancel and retry.
import { useEffect, useMemo, useRef, useState, useSyncExternalStore, type CSSProperties, type MouseEvent } from "react";
import { thumbHashToDataURL } from "thumbhash";
import { session } from "../api";
import {
  aspectRatio,
  formatBytes,
  gridColumns,
  mediaLabel,
  urlExpired,
  type Media,
  type MediaLimits,
} from "../chat/media";
import { AttachmentTray, trayStatus, type TraySnapshot } from "../chat/tray";
import { browserTrayDeps } from "../chat/upload";

const placeholders = new Map<string, string>();

/** The ThumbHash as a tiny image URL, a blurred stand-in while the thumbnail loads. */
function placeholder(hash: string | null): string | undefined {
  if (!hash) return undefined;
  let url = placeholders.get(hash);
  if (url === undefined) {
    try {
      url = thumbHashToDataURL(Uint8Array.from(atob(hash), (c) => c.charCodeAt(0)));
    } catch {
      url = "";
    }
    if (placeholders.size > 500) placeholders.clear();
    placeholders.set(hash, url);
  }
  return url || undefined;
}

/** Calls `onExpired` when a media element failed because its signed URL ran out. */
function expiredHandler(url: string, onExpired: (() => void) | undefined) {
  return () => {
    if (urlExpired(url, Date.now())) onExpired?.();
  };
}

export function MessageMedia(props: { attachments: readonly Media[]; onExpired?: (() => void) | undefined }) {
  const { attachments, onExpired } = props;
  const [open, setOpen] = useState<number | null>(null);
  const visual = attachments.filter((a) => a.kind !== "file");
  const files = attachments.filter((a) => a.kind === "file");
  const images = visual.filter((a) => a.kind === "image");
  const single = visual.length === 1;

  return (
    <div className="chat-media">
      {visual.length > 0 && (
        <div
          className={`chat-media-grid${single ? " single" : ""}`}
          style={
            {
              "--columns": gridColumns(visual.length),
              // A small photo or video is never stretched past its own width.
              ...(single && visual[0]!.width ? { maxWidth: `${visual[0]!.width}px` } : {}),
            } as CSSProperties
          }
        >
          {visual.map((media) => {
            const hash = placeholder(media.thumbhash);
            const style: CSSProperties = {
              aspectRatio: String(aspectRatio(media, single)),
              ...(hash ? { backgroundImage: `url(${hash})` } : {}),
            };
            if (media.kind === "video") {
              return (
                <video
                  key={media.id}
                  className="chat-media-tile"
                  style={style}
                  src={media.url}
                  poster={media.thumb_url ?? undefined}
                  controls
                  playsInline
                  preload="none"
                  aria-label={mediaLabel("video", 1)}
                  onError={expiredHandler(media.url, onExpired)}
                />
              );
            }
            const src = media.thumb_url ?? media.url;
            return (
              <button
                type="button"
                key={media.id}
                className="chat-media-tile"
                style={style}
                aria-label="Open photo"
                onClick={() => setOpen(images.indexOf(media))}
              >
                <img
                  src={src}
                  alt=""
                  loading="lazy"
                  decoding="async"
                  // The placeholder would show through a transparent photo.
                  onLoad={(event) => (event.currentTarget.parentElement!.style.backgroundImage = "none")}
                  onError={expiredHandler(src, onExpired)}
                />
              </button>
            );
          })}
        </div>
      )}
      {files.map((media) => (
        <FileChip key={media.id} media={media} onExpired={onExpired} />
      ))}
      {open !== null && images[open] && (
        <Lightbox images={images} index={open} onIndex={setOpen} onClose={() => setOpen(null)} onExpired={onExpired} />
      )}
    </div>
  );
}

/**
 * A download link for a signed URL, which lasts about an hour: a click after
 * that asks for the message again and downloads once the fresh URL is in.
 */
function useFreshDownload(url: string, onExpired: (() => void) | undefined) {
  const ref = useRef<HTMLAnchorElement>(null);
  /** The expired URL a click asked to replace, and the one already downloaded for. */
  const [expiredUrl, setExpiredUrl] = useState<string | null>(null);
  const handled = useRef<string | null>(null);

  useEffect(() => {
    if (expiredUrl === null || expiredUrl === url || handled.current === expiredUrl) return;
    handled.current = expiredUrl;
    ref.current?.click();
  }, [expiredUrl, url]);

  return {
    ref,
    waiting: expiredUrl !== null && expiredUrl === url,
    onClick: (event: MouseEvent<HTMLAnchorElement>) => {
      if (urlExpired(url, Date.now(), 5_000) && onExpired) {
        event.preventDefault();
        setExpiredUrl(url);
        onExpired();
      }
    },
  };
}

function FileChip(props: { media: Media; onExpired: (() => void) | undefined }) {
  const { media } = props;
  const { ref, waiting, onClick } = useFreshDownload(media.url, props.onExpired);
  return (
    <a
      ref={ref}
      className="chat-file"
      href={media.url}
      download={media.file_name ?? ""}
      aria-busy={waiting}
      onClick={onClick}
    >
      <span className="chat-file-icon" aria-hidden="true">
        📄
      </span>
      <span className="chat-file-name">{media.file_name ?? "File"}</span>
      <span className="chat-file-size">{waiting ? "Preparing…" : formatBytes(media.bytes)}</span>
    </a>
  );
}

function Lightbox(props: {
  images: readonly Media[];
  index: number;
  onIndex: (index: number) => void;
  onClose: () => void;
  onExpired: (() => void) | undefined;
}) {
  const { images, index, onIndex, onClose } = props;
  const ref = useRef<HTMLDialogElement>(null);
  const media = images[index]!;
  const many = images.length > 1;
  const { ref: downloadRef, waiting: downloading, onClick: onDownload } = useFreshDownload(media.url, props.onExpired);

  useEffect(() => {
    const dialog = ref.current;
    if (dialog && !dialog.open) dialog.showModal();
  }, []);

  function step(by: number) {
    onIndex((index + by + images.length) % images.length);
  }

  return (
    <dialog
      ref={ref}
      className="chat-lightbox"
      aria-label={many ? `Photo ${index + 1} of ${images.length}` : "Photo"}
      onClose={onClose}
      onClick={(event) => {
        // A click on the backdrop (the dialog itself, not its content) closes it.
        if (event.target === event.currentTarget) onClose();
      }}
      onKeyDown={(event) => {
        if (!many) return;
        if (event.key === "ArrowLeft") step(-1);
        else if (event.key === "ArrowRight") step(1);
      }}
    >
      <img
        src={media.url}
        alt=""
        style={{ backgroundImage: placeholder(media.thumbhash) ? `url(${placeholder(media.thumbhash)})` : undefined }}
        onError={expiredHandler(media.url, props.onExpired)}
      />
      <div className="chat-lightbox-bar">
        {many && (
          <button type="button" onClick={() => step(-1)} aria-label="Previous photo">
            ‹
          </button>
        )}
        {many && (
          <span>
            {index + 1} / {images.length}
          </span>
        )}
        {many && (
          <button type="button" onClick={() => step(1)} aria-label="Next photo">
            ›
          </button>
        )}
        <a
          className="button-link"
          href={media.url}
          download=""
          ref={downloadRef}
          aria-busy={downloading}
          onClick={onDownload}
        >
          {downloading ? "Preparing…" : "Download"}
        </a>
        <button type="button" onClick={onClose} autoFocus>
          Close
        </button>
      </div>
    </dialog>
  );
}

/** The composer's attachment tray for one conversation (cancelled when it changes or unmounts). */
export function useAttachmentTray(conversationId: string): { tray: AttachmentTray; snapshot: TraySnapshot } {
  const tray = useMemo(() => new AttachmentTray(browserTrayDeps(session, conversationId)), [conversationId]);
  useEffect(() => {
    tray.open();
    return () => tray.close();
  }, [tray]);
  const snapshot = useSyncExternalStore(tray.subscribe, tray.getSnapshot);
  return { tray, snapshot };
}

export function TrayView(props: { tray: AttachmentTray; snapshot: TraySnapshot; limits: MediaLimits }) {
  const { tray, snapshot, limits } = props;
  const status = useMemo(() => trayStatus(snapshot.items), [snapshot.items]);
  if (snapshot.items.length === 0 && !snapshot.notice) return null;
  return (
    <div className="chat-tray">
      {snapshot.notice && (
        <p className="chat-tray-notice error" role="alert">
          {snapshot.notice}{" "}
          <button type="button" className="link-button" onClick={() => tray.dismissNotice()}>
            Dismiss
          </button>
        </p>
      )}
      {snapshot.items.length > 0 && (
        <ul className="chat-tray-items" aria-label="Attachments">
          {snapshot.items.map((item) => {
            const busy = item.status !== "done" && item.status !== "failed";
            return (
              <li key={item.key} className={`chat-tray-item ${item.status}`}>
                <span className="chat-tray-preview" aria-hidden="true">
                  {item.previewUrl ? (
                    <img src={item.previewUrl} alt="" />
                  ) : item.kind === "video" ? (
                    "🎬"
                  ) : item.kind === "image" ? (
                    "🖼️"
                  ) : (
                    "📄"
                  )}
                </span>
                <span className="chat-tray-text">
                  <span className="chat-tray-name">{item.name || mediaLabel(item.kind, 1)}</span>
                  <span className={`chat-tray-state${item.status === "failed" ? " error" : ""}`}>
                    {item.status === "failed"
                      ? item.error
                      : item.status === "done"
                        ? formatBytes(item.media?.bytes ?? item.size)
                        : item.status === "uploading"
                          ? `${Math.round(item.progress * 100)}%`
                          : item.status === "preparing"
                            ? "Preparing…"
                            : "Waiting…"}
                  </span>
                  {busy && (
                    <progress
                      max={1}
                      value={item.status === "uploading" ? item.progress : undefined}
                      aria-label={`Uploading ${item.name}`}
                    />
                  )}
                </span>
                {item.status === "failed" && (
                  <button type="button" className="link-button" onClick={() => tray.retry(item.key, limits)}>
                    Retry
                  </button>
                )}
                <button
                  type="button"
                  className="chat-icon-button"
                  aria-label={busy ? `Cancel uploading ${item.name}` : `Remove ${item.name}`}
                  onClick={() => tray.remove(item.key)}
                >
                  ×
                </button>
              </li>
            );
          })}
        </ul>
      )}
      {status && (
        <p className="chat-tray-status hint" aria-live="polite">
          {status}
        </p>
      )}
    </div>
  );
}

/** The paperclip: picks photos, videos or files. */
export function AttachButton(props: { onFiles: (files: File[]) => void; disabled?: boolean }) {
  const input = useRef<HTMLInputElement>(null);
  return (
    <>
      <button
        type="button"
        className="chat-icon-button chat-attach"
        aria-label="Attach photos, videos or files"
        title="Attach"
        disabled={props.disabled}
        onClick={() => input.current?.click()}
      >
        📎
      </button>
      <input
        ref={input}
        type="file"
        multiple
        hidden
        onChange={(event) => {
          const files = [...(event.target.files ?? [])];
          event.target.value = "";
          if (files.length) props.onFiles(files);
        }}
      />
    </>
  );
}
